import { useEffect, useRef, useState, useCallback } from 'react';
import { createConsumer } from '@rails/actioncable';
import { getConfig } from '../utils/config';
import { refreshAccessToken } from '../utils/tokenRefresh';
import { getToken as getAuthToken } from '../stores/authStore';

/**
 * Hook for real-time project collaboration via Action Cable
 */
export function useProjectActionCable(projectId, onEvent) {
  const subscriptionRef = useRef(null);
  const consumerRef = useRef(null);
  const retriedRef = useRef(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const buildCableUrl = (token) => {
      const apiUrl = getConfig('API_URL') || 'https://localhost:3000/api/v1';
      return apiUrl
        .replace('/api/v1', '')
        .replace('https://', 'wss://')
        .replace('http://', 'ws://') + `/cable?token=${token}`;
    };

    const connectToCable = () => {
      try {
        const token = getAuthToken();
        if (!token) return;

        if (consumerRef.current) consumerRef.current.disconnect();

        const consumer = createConsumer(buildCableUrl(token));
        consumerRef.current = consumer;

        const subscription = consumer.subscriptions.create(
          { channel: 'ProjectChannel', project_id: projectId },
          {
            connected() {
              setIsConnected(true);
              setConnectionError(null);
              retriedRef.current = false;
            },

            disconnected() {
              setIsConnected(false);
            },

            rejected() {
              setIsConnected(false);
              if (!retriedRef.current) {
                retriedRef.current = true;
                refreshAccessToken().then((newToken) => {
                  if (newToken) connectToCable();
                  else setConnectionError('Subscription rejected');
                }).catch(() => {
                  // Renewal is rate limited or briefly unreachable. Surface it as
                  // a connection problem, which is what it is, rather than
                  // letting the rejection go unhandled. `retriedRef` has already
                  // been spent, so nothing reconnects on its own from here - say
                  // that plainly instead of promising a retry that never comes.
                  setConnectionError('Connection unavailable. Reload to reconnect.');
                });
              } else {
                setConnectionError('Subscription rejected');
              }
            },

            received(data) {
              if (onEvent) onEvent(data);
            }
          }
        );

        subscriptionRef.current = subscription;
      } catch (error) {
        setConnectionError(error.message);
        setIsConnected(false);
      }
    };

    connectToCable();

    return () => {
      if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
      if (consumerRef.current) consumerRef.current.disconnect();
    };
  }, [projectId, onEvent]);

  // Send typing notification
  const sendTyping = useCallback((chatId) => {
    if (subscriptionRef.current && isConnected) {
      subscriptionRef.current.perform('typing', { chat_id: chatId });
    }
  }, [isConnected]);

  // Send stopped typing notification
  const sendStoppedTyping = useCallback((chatId) => {
    if (subscriptionRef.current && isConnected) {
      subscriptionRef.current.perform('stopped_typing', { chat_id: chatId });
    }
  }, [isConnected]);

  return {
    isConnected,
    connectionError,
    sendTyping,
    sendStoppedTyping
  };
}

export default useProjectActionCable;
